/**
 * CL-PROPERTY-OS-002S — Block authenticated portal workspace routes until email verification policy is satisfied.
 */

import {
  classifyPortalEmailVerificationRolloutFromUser,
  portalEmailVerificationSatisfiedFromUser,
} from "./portalEmailVerificationRolloutPolicy";
import { shouldDeferPortalReadsForInviteInUrl } from "./portalInviteUrlAccessGate";

function safeStr(value) {
  return String(value ?? "").trim();
}

/** True for `/client-portal` index (sign-in / sign-up / verification lifecycle shell). */
export function isClientPortalAuthEntryPath(pathname = "") {
  const normalized = safeStr(pathname).replace(/\/+$/, "") || "/";
  return normalized === "/client-portal";
}

/**
 * Auth-only gate: defer CRM/profile/invitation/workspace Firestore reads until verification policy allows access.
 */
export function shouldDeferPortalFirestoreReads(
  user = null,
  { isCurrentSignupSession = false, pendingInviteCode = "", pendingSignupContext = null } = {}
) {
  if (!user || user.isAnonymous) return false;
  if (
    shouldDeferPortalReadsForInviteInUrl(user, {
      pendingInviteCode,
      isCurrentSignupSession,
      pendingSignupContext,
    })
  ) {
    return true;
  }
  return !portalEmailVerificationSatisfiedFromUser(user, { isCurrentSignupSession });
}

export function resolvePortalAuthEntryAccessState(
  user = null,
  { isCurrentSignupSession = false, pendingInviteCode = "", pendingSignupContext = null } = {}
) {
  if (!user || user.isAnonymous) {
    return {
      deferFirestoreReads: false,
      showVerificationPanel: false,
      clientErrorMessage: "",
    };
  }
  const classification = classifyPortalEmailVerificationRolloutFromUser(user, { isCurrentSignupSession });
  return {
    deferFirestoreReads: shouldDeferPortalFirestoreReads(user, {
      isCurrentSignupSession,
      pendingInviteCode,
      pendingSignupContext,
    }),
    showVerificationPanel: classification.showVerificationPanel === true,
    clientErrorMessage: safeStr(classification.clientErrorMessage),
  };
}

/**
 * When true, workspace child routes must not mount or perform Firestore reads.
 * Auth entry (`/client-portal`) keeps ClientPortal responsible for signup + verification lifecycle UI.
 */
export function shouldBlockPortalWorkspaceAccess(user = null, { pathname = "" } = {}) {
  if (!user || user.isAnonymous) return false;
  if (isClientPortalAuthEntryPath(pathname)) return false;
  return shouldDeferPortalFirestoreReads(user);
}

/** True when project/workspace loaders should wait or skip (unverified session). */
export function shouldDeferPortalWorkspaceDataLoad(authUser = null, options = {}) {
  if (!authUser) return true;
  if (authUser.isAnonymous) return false;
  return shouldDeferPortalFirestoreReads(authUser, options);
}
